import { API_BASE_URL } from "./apiService";
import { apiFetch } from "./apiFetch";

async function postJSON(path, body) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok || !data || !data.success) {
    const error = new Error(data?.error || "Request failed");
    error.status = response.status;
    if (Array.isArray(data?.errors)) error.errors = data.errors;
    throw error;
  }

  return data;
}

// Logs in a user with email and password against the backend JWT endpoint.
// Patient/therapist accounts come back as { mfaRequired: true, email } instead
// of a session — a 6-digit code is emailed, which is then submitted via
// loginVerifyOtp to actually complete the login. There's no
// dedicated resend endpoint - resending is just calling loginUser again.
export const loginUser = async (email, password) => {
  const data = await postJSON("/auth/login", { email, password });
  return data.data; // { token, refreshToken, user } or { mfaRequired: true, email }
};

// Submit the code from the login email. Completes the session on success.
export const loginVerifyOtp = (email, otp) =>
  postJSON("/auth/login/verify-otp", { email, otp }).then((d) => d.data); // { token, refreshToken, user }

// --- Registration (emailed code -> details) ---

// Step 1: kick off registration. Always 200 — branch the UI on the returned
// shape: {accountExists:false, otpSent:true} for a brand new email (a 6-digit
// code is emailed), or {accountExists:true, canApplyAsTherapist} for one
// that's already in use.
export const registerStart = (email, language = "en") =>
  postJSON("/auth/register/start", { email, language }).then((d) => d.data);

// Step 2: submit the code from the registration email.
export const registerVerifyOtp = (email, otp) =>
  postJSON("/auth/register/verify-otp", { email, otp }).then((d) => d.data);

// Step 3: set the password (+ optional profile/consent fields), creates the
// account, returns a full session (auto-login). Must be called within 15
// minutes of a successful verify-otp for the same email.
export const registerComplete = ({
  email,
  otp,
  password,
  name,
  countryId,
  languageId,
  consentPortalUpdates,
  consentMarketing,
}) =>
  postJSON("/auth/register/complete", {
    email,
    otp,
    password,
    countryId,
    languageId,
    ...(name ? { name } : {}),
    consentPortalUpdates: Boolean(consentPortalUpdates),
    consentMarketing: Boolean(consentMarketing),
  }).then((d) => d.data); // { token, refreshToken, user }

// --- Password reset (emailed code -> new password) ---

// Emails the reset code; returns the confirmationId the next steps need.
export const forgotPassword = (email) =>
  postJSON("/auth/forgot-password", { email }).then((d) => d.data); // { confirmationId }

// Submit the code from the reset email.
export const resetPasswordConfirmCode = (confirmationId, email, code) =>
  postJSON("/auth/reset-password/confirm-code", { confirmationId, email, code }).then(
    (d) => d.data
  ); // { confirmed: true }

// Set the new password. No session is returned — user must log in again.
export const resetPasswordComplete = (confirmationId, email, password) =>
  postJSON("/auth/reset-password/complete", { confirmationId, email, password });

// Authenticated password change — requires a valid JWT and the current password.
export const changePassword = (previousPassword, newPassword) =>
  apiFetch("/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ previousPassword, newPassword }),
  });
